import type { SectionCoverageRow, SupportingPagesV2RowResult } from '../services/dataforseo';

/**
 * Which sections of the rival's page yours does not cover.
 *
 * One definition, shared. It was previously private to
 * `plan-v2-work-item-compose.ts`, so the Dashboard recap had no way to name the
 * same sections the Expand view lists and the work-item composer writes up —
 * and a recap that counted gaps differently from the panel behind it is the
 * bug this file exists to prevent. `missingSectionCount` on the stored eval row
 * is NOT that number: measured on 326 live snapshots it disagrees with this
 * selection on 22 of them, because it counts groups before the two filters
 * below are applied.
 *
 * The filters, and why each is not optional:
 *
 * - **`listicleSuppressed`** — on a listicle the analyzer emits one section per
 *   tool the rival lists. Those are inventory, not structure; surfacing them as
 *   "sections you never mention" tells an operator to write twenty headings
 *   when the finding is "they list twenty tools". `listInventoryDiff` is where
 *   that belongs.
 * - **covered but with `absentTerms`** — kept, deliberately. A section your
 *   page addresses while missing every salient phrase in it is a real gap; it
 *   is just a thinner one than a section you never wrote.
 * - **`isNonTopical`** — an intro, an author bio, a "related posts" block. The
 *   field's own contract is "excluded from gap counts", and telling an operator
 *   to write an author bio is the kind of suggestion that costs trust in every
 *   other suggestion on the page.
 *
 *   It used to be belt-and-braces: the analyzer forces `sectionCovered` true
 *   and `absentTerms` empty for the reasons IT assigns, so the two filters
 *   above already dropped them. **Since §0aq it is load-bearing.**
 *   `rival_brand_heading` and `rival_no_outline` are assigned backend-side —
 *   the analyzer is handed two bodies and cannot see the rival's domain or the
 *   focal URL — and they leave `sectionCovered` and `absentTerms` exactly as
 *   the analyzer reported them, so this line is the only thing keeping "Get the
 *   latest from StreamWork" off the list.
 */
/**
 * The rule itself, over a plain list of coverage groups.
 *
 * Exposed separately because Quick Compare holds its groups on its own state
 * rather than on an eval row, and the alternative was casting a fake row to
 * reach this filter — which is how a fourth definition gets written.
 */
export function sectionGaps(
  groups: readonly SectionCoverageRow[] | null | undefined
): SectionCoverageRow[] {
  return [...(groups ?? [])]
    .filter((sc) => !sc.listicleSuppressed)
    .filter((sc) => !sc.isNonTopical)
    .filter((sc) => !isPageTitleHeading(sc))
    .filter((sc) => !sc.sectionCovered || (sc.absentTerms?.length ?? 0) > 0)
    .sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
}

/**
 * A level-1 heading is the rival's PAGE, not a section inside it.
 *
 * The analyser emits the H1 as section #1 and compares it like any other
 * heading, so "A Beginner's Guide to Service Delivery Models" — the entire
 * title of argano.com's article — arrived as a section this page was missing.
 * Writing a section named after someone's whole article is not a task.
 *
 * It is not a rare edge: **217 of 1,664** section-gap rows in the pilot corpus
 * are the rival's H1, and it drags in worse than titles. On pages where a
 * reviews widget uses H1s, the "sections you don't have" were app-store
 * reviews — "Never thought learning could be so fun!", "I love Trailhead,
 * but…".
 *
 * Read from `competitorHeadingLevel`, falling back to the gap group's own last
 * ancestor. Both are present on every grouped row in the corpus and agree on
 * all 128 level-1 rows sampled; the fallback is here because a row is not
 * obliged to carry the flat field and dropping the check would silently
 * readmit page titles.
 */
export function isPageTitleHeading(section: SectionCoverageRow | null | undefined): boolean {
  if (!section) return false;
  if (section.competitorHeadingLevel === 1) return true;
  const ancestors = section.gapAncestorSegments || [];
  return ancestors[ancestors.length - 1]?.level === 1;
}

export function sectionGapsForV2Row(
  row: SupportingPagesV2RowResult | null | undefined
): SectionCoverageRow[] {
  return sectionGaps(row?.sectionCoverageGroups);
}

/** Whether a rival's eval row has any section this page is missing. */
export function hasSectionGapsForV2Row(
  row: SupportingPagesV2RowResult | null | undefined
): boolean {
  return sectionGapsForV2Row(row).length > 0;
}

/**
 * The heading a collapsed gap group is filed under, or null for a plain row.
 *
 * The last ancestor segment: for a single-member group that is the section's
 * own heading, and for a multi-member group it is the parent the members sit
 * under. Both are what the compare view renders in bold.
 */
export function sectionGapGroupHeading(section: SectionCoverageRow): string | null {
  if (!section?.isGapGroup) return null;
  const ancestors = section.gapAncestorSegments || [];
  const last = ancestors[ancestors.length - 1]?.heading;
  return (last || '').trim() || null;
}

/**
 * The sibling sections a group collapsed, when it collapsed more than one.
 *
 * Empty for a single-member group, because there the group heading IS the
 * section and listing it again under itself says nothing.
 */
export interface SectionGapMember {
  heading: string;
  /** Heading level on the rival's page (2 = H2, 3 = H3). Null when unknown. */
  level: number | null;
}

export function sectionGapGroupMembers(section: SectionCoverageRow): SectionGapMember[] {
  if (!section?.isGapGroup) return [];
  const paths = section.gapMemberPaths || [];
  if (paths.length < 2) return [];
  const seen = new Set<string>();
  const out: SectionGapMember[] = [];
  for (const path of paths) {
    const segs = path.headingPathSegments || [];
    const leaf = segs[segs.length - 1];
    const clean = (leaf?.heading || '').replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '');
    if (!clean || seen.has(clean.toLowerCase())) continue;
    seen.add(clean.toLowerCase());
    out.push({ heading: clean, level: typeof leaf?.level === 'number' ? leaf.level : null });
  }
  return out;
}

/**
 * The heading level of the gap itself — "H2" beside "Project scope examples".
 *
 * These rows have always BEEN the rival's headings, and nothing on the card
 * said so: a run of plain text under a checkbox reads as a list someone typed,
 * not as the shape of a page. The level is what turns it back into structure,
 * and it is what tells an operator whether they are being asked for a section
 * or for a sub-point inside one.
 */
export function sectionGapLevel(section: SectionCoverageRow): number | null {
  const grouped = sectionGapGroupHeading(section);
  if (grouped) {
    const ancestors = section.gapAncestorSegments || [];
    const last = ancestors[ancestors.length - 1];
    return typeof last?.level === 'number' ? last.level : null;
  }
  return typeof section?.competitorHeadingLevel === 'number'
    ? section.competitorHeadingLevel
    : null;
}

/**
 * The nearest thing on your page to one of their sections.
 *
 * Extracted so the review card and Quick Compare cannot disagree about whether
 * a match exists — the whole point of this file. `sectionSimilarity` alone is
 * not the test: a row can carry a score with no heading to attach it to.
 */
export function sectionGapHasCloseMatch(sc: SectionCoverageRow): boolean {
  return !!(sc.closestMyHeading && (sc.sectionSimilarity || 0) > 0);
}

export function sectionGapClosestPct(sc: SectionCoverageRow): number {
  return Math.round((sc.sectionSimilarity || 0) * 100);
}

export function sectionGapClosestHeading(sc: SectionCoverageRow): string {
  return sc.closestMyHeadingPath || sc.closestMyHeading || '';
}

/**
 * What the rival's section is actually called.
 *
 * NOT `competitorHeading` on a grouped row. When consecutive sibling sections
 * are collapsed into one gap, that field keeps the FIRST member's leaf heading
 * while the gap itself is the parent — so a page missing all three of Adobe's
 * "Project scope examples" was reported as missing "Website development
 * project", one example out of three, with the other two invisible.
 *
 * The count already came from the grouped rows, which is how the card ended up
 * saying "2 sections" over labels naming something else: one fact, two
 * derivations. Compare has always rendered the group's own heading, so reading
 * it here is what makes the two surfaces agree.
 *
 * Long headings are truncated on a word boundary; chips are a glance.
 */
export function sectionGapLabel(section: SectionCoverageRow, maxChars = 42): string {
  const heading = (sectionGapGroupHeading(section) || section.competitorHeading || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!heading) return 'Untitled section';
  // Trailing full stops are common in these headings ("Establish a change
  // control process.") and read as a sentence fragment in a chip row.
  const clean = heading.replace(/[.\s]+$/, '');
  if (clean.length <= maxChars) return clean;
  const cut = clean.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxChars * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
